<template>
    <!--
        Click-or-drop image picker. The dropzone is a label wrapping a
        hidden file input — clicking the label opens the system file
        picker, dropping files fires the dragover/drop handlers. Both
        funnel into the `add` event so the parent does the same thing
        regardless of how the user added the images.
    -->
    <label
        class="dropzone"
        :class="{ 'is-dragover': isDragOver }"
        :aria-disabled="disabled"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
    >
        <span class="dropzone-icon" aria-hidden="true">📷</span>
        <span class="dropzone-text">
            Drop images here or <strong>browse</strong>
        </span>
        <span class="dropzone-hint">JPG, PNG, HEIC, WebP</span>
        <input
            ref="fileInput"
            type="file"
            accept="image/*"
            multiple
            class="dropzone-input"
            :disabled="disabled"
            @change="onPick"
        />
    </label>

    <!--
        Thumbnails of already-added images. Each shows a small ×
        button to remove it. URL.createObjectURL is cheaper than
        re-reading the file as a data URL — revoked when the
        component unmounts to avoid the URLs leaking forever.
    -->
    <div v-if="images.length > 0" class="thumbs">
        <div v-for="(img, idx) in images" :key="idx" class="thumb">
            <img :src="previews[idx]" :alt="img.name" />
            <span
                class="thumb-remove"
                role="button"
                :aria-label="`Remove ${img.name}`"
                @click="$emit('remove', idx)"
            >×</span>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'

const props = defineProps({
    images: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['add', 'remove'])

const fileInput = ref(null)
const isDragOver = ref(false)

// One object URL per File, recomputed on every images-array change.
// Held in a separate computed so the template can index into them
// without re-creating the URL on each render.
const previews = computed(() =>
    (props.images || []).map((f) =>
        f instanceof File ? URL.createObjectURL(f) : ''
    )
)

// Revoke object URLs when the component goes away. Createt-then-
// abandoned URLs hold the file in memory until the page unloads,
// which adds up if the user opens/closes the widget repeatedly.
onBeforeUnmount(() => {
    for (const url of previews.value) {
        if (url) URL.revokeObjectURL(url)
    }
})

function onPick(e) {
    if (props.disabled) return
    const files = Array.from(e.target.files || [])
    if (files.length) emit('add', files)
    // Reset so picking the same file twice still fires `change`.
    e.target.value = ''
}

function onDragOver() {
    if (props.disabled) return
    isDragOver.value = true
}
function onDragLeave() {
    isDragOver.value = false
}
function onDrop(e) {
    isDragOver.value = false
    if (props.disabled) return
    const files = Array.from(e.dataTransfer.files || []).filter(
        (f) => f.type && f.type.startsWith('image/')
    )
    if (files.length) emit('add', files)
}
</script>
